import React, { useState } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { getPromotionMainColor, i18nIntl, isEmpty, isNotEmpty, minutesToNextHour } from '../../lib/ep.js';
import { stage_types as stage_types } from '../../data/bootstrap_stages';

import ActionLink from '../builder/ActionLink';
import Spinner from '../builder/Spinner';

import FloatingLayerPublic from './FloatingLayerPublic';
import StageImage from './StageImage';

import { canParticipate } from '../../containers/Stage.js';
import LeveledHTMLText from './helpers/LeveledHTMLText.js';
import RibbonCollection from '../ui_items/RibbonCollection.js';

const ViewMyParticipationLink = ({ promo_id, stage, ...props }) => {
	const is_builder = props?.is_builder ?? false;

	return (
		<ActionLink
			_href={(!is_builder) ? `/users/my_participation/${promo_id}` : null}
			_class={{other:'hb_mainColorText'}}
			stopPropagation={true}
		>View my participations</ActionLink>
	)
};

const ViewGalleryLink = ({ promo_id, stage, ...props }) => {
	const is_voting_type = stage?.is_voting ?? false;

	if(is_voting_type === false) return null;

	const is_builder = props?.is_builder ?? false;

	return (
		<ActionLink
			_href={(!is_builder) ? `/gallery/${promo_id}/${stage.id}` : null}
			_class={{other:'hb_mainColorText'}}
			stopPropagation={true}
		>View gallery</ActionLink>
	)
};

const ViewGalleryRankingLink = ({ promo_id, stage, ...props }) => {
	const is_voting_type = stage?.is_voting ?? false;
	const has_voting_raking_enabled = stage?.voting_enable_ranking ?? false;

	if(is_voting_type === false || has_voting_raking_enabled === false) return null;

	const is_builder = props?.is_builder ?? false;

	return (
		<ActionLink
			_href={(!is_builder) ? `/gallery/${promo_id}/ranking/${stage.id}` : null}
			_class={{other:'hb_mainColorText'}}
			stopPropagation={true}
		>View ranking</ActionLink>
	)
};

const ViewStatsLink = ({ promo_id, stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_team_type = stage?.is_team ?? false;
	const has_statistics_enabled = stage?.team_stats_publish ?? '0';

	if(is_team_type === false || has_statistics_enabled == '0') return null;

	const is_builder = props?.is_builder ?? false;

	return (
		<ActionLink
			_href={(!is_builder) ? `/stats/${promo_id}/${stage.id}` : null}
			_class={{other:'hb_mainColorText'}}
			autotranslate={false}
			stopPropagation={true}
		>{stage?.team_stats_button ?? i18n("See statistics")}</ActionLink>
	)
};

const ViewResultLink = ({ promo_id, stage, ...props }) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const is_team_type = stage?.is_team ?? false;
	const has_results_enabled = stage?.team_results_publish ?? '0';

	if(is_team_type === false || has_results_enabled == '0') return null;

	const is_builder = props?.is_builder ?? false;

	return (
		<ActionLink
			_href={(!is_builder) ? `/results/${promo_id}/${stage.id}` : null}
			_class={{other:'hb_mainColorText'}}
			autotranslate={false}
			stopPropagation={true}
		>{stage?.team_result_button ?? i18n("See winner team")}</ActionLink>
	)
};

function StageMosaicItem(props) {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const [ opening, setOpening ] = useState(false);
	const [ opening_loading, setOpeningLoading ] = useState(false);
	const [ display_no_participations_layer, setDisplayNoParticipationsLayer ] = useState(false);
	const [ display_completed_layer, setDisplayNoCompletedLayer ] = useState(false);
	const [ display_no_more_prizes_layer, setDisplayNoMorePrizesLayer ] = useState(false);
	const [ display_closed_gallery_layer, setDisplayClosedGalleryLayer ] = useState(false);
	const [ display_segment_disabled_layer, setDisplaySegmenDisabledLayer ] = useState(false);

	const stage = props?.stage ?? false;
	const is_builder = props?.is_builder ?? false;
	const force_openable = props?.force_openable ?? false;
	const brackets_list = props?.brackets_list ?? false;
	const force_view = (is_builder) ? (props?.force_view ?? false) : false;
	const in_galleries = props?.in_galleries ?? false;
	const participation = (props.participations != undefined) ? props.participations[stage.id] : null;
	const promo = props?.promo ?? false;
	if(!stage || !promo) return null;

	const segment_disabled = stage?.segment_disabled ?? false;
	const hide_stage = stage?.hide_stage ?? false;
	const is_disabled_no_prizes = stage?.no_prize_closed ?? false;
	
	const stages_mosaic_hide_expired_stages = promo?.stages_mosaic_hide_expired_stages ?? false;
	const site_layout = promo?.stages_mosaic_layout ?? 'mosaic';	// "mosaic" / "list"
	const list_graphic_display = (site_layout == 'list') ? (promo?.stages_mosaic_list_graphic_display ?? 'icon') : 'icon';
	const hide_stage_status = promo?.stages_mosaic_hide_status ?? false;
	const hide_completion_status = promo?.stages_mosaic_hide_completion_status ?? true;
	const close_on_completion = stage?.close_on_completion ?? false;

	let no_more_participations_completed = false;

	const stage_type = stage_types.find((f) => (f.id == stage.promotion_type_id) ? true : false);
	if(stage_type == undefined) return null;
	const is_album = stage?.is_voting ?? false;	// Multimedia gallery

	const tz = promo.timezone;
	const [ open, closed, pending, can_participate_again ] = canParticipate(tz, participation, stage, is_builder);

	const completed = participation?.completed ?? false;
	let stage_state;
	if((open || in_galleries) && !is_disabled_no_prizes) {
		stage_state = (can_participate_again) ? 'open' : ((!is_album) ? 'open_but' : 'open_but_not_disabled');
		if(completed && close_on_completion) {
			if(isEmpty(stage, 'stage_picture_completed')) stage_state = 'open_but';
			no_more_participations_completed = true;
		}
	} else if(closed || is_disabled_no_prizes) {
		stage_state = 'closed'
	} else {
		stage_state = 'pending';
	}

	if(!is_builder && (hide_stage || (stages_mosaic_hide_expired_stages && closed && !force_view))) return null;

	const hide_stage_name = (site_layout == 'mosaic' && promo.stages_mosaic_hide_name != undefined) ? promo.stages_mosaic_hide_name : false;
	let hide_participation_info = (promo.stages_mosaic_hide_stage_participations != undefined) ? promo.stages_mosaic_hide_stage_participations : false;
	const stage_title = (!hide_stage_name && stage.display_title != undefined && stage.display_title != "") ? stage.display_title : false;
	const stage_description = (!hide_stage_name && stage.display_description != undefined && stage.display_description != "") ? stage.display_description : false;

	let default_cta_label = i18n("Participate");
	let force_access = false;
	let display_closed_gallery = false;
	if(stage.type == '8') {
		force_access = true;
		if(stage.voting_enabled != undefined && stage.voting_enabled) {
			if(open) {
				default_cta_label = i18n("vote", 'ucfirst');
			} else {
				default_cta_label = i18n("View");
				if(!is_builder) display_closed_gallery = true;
			}
		} else {
			default_cta_label = i18n("View");
			hide_participation_info = true;
		}
	}
	const enter_button_label = (isNotEmpty(promo, 'stages_mosaic_button_label')) ? promo.stages_mosaic_button_label : default_cta_label;

	let images_for_closed_stage = false;
	let stage_picture = false;
	if(stage.stage_picture != undefined && stage.stage_picture != "") {
		stage_picture =  stage.full_stage_picture;
	}

	if(completed && !closed && isNotEmpty(stage, 'stage_picture_completed')) {
		stage_picture = stage.full_stage_picture_completed;
		images_for_closed_stage = true;
	} else if(!can_participate_again && isNotEmpty(stage, 'stage_picture_closed_participations')) {
		stage_picture = stage.full_stage_picture_closed_participations;
		images_for_closed_stage = true;
	} else if(closed && isNotEmpty(stage, 'stage_picture_closed_date')) {
		stage_picture = stage.full_stage_picture_closed_date;
		images_for_closed_stage = true;
	}

	const show_number_participants = stage?.show_number_participants ?? false;

	const has_participated = participation?.has_participated ?? false;

	const default_promo_color = getPromotionMainColor(promo);

	const animation_type = (promo.stages_mosaic_open_animation != undefined && promo.stages_mosaic_open_animation != 'none') ? props.promo.stages_mosaic_open_animation : false;

	const openStage = () => {
		if(!is_builder && completed && close_on_completion) {
			setDisplayNoCompletedLayer(true);
			return;
		}

		if(!is_builder && !can_participate_again) {
			setDisplayNoParticipationsLayer(true);
		} else if(!is_builder && is_disabled_no_prizes) {
			setDisplayNoMorePrizesLayer(true);
		} else if(display_closed_gallery) {
			setDisplayClosedGalleryLayer(true);
		} else if(segment_disabled) {
			setDisplaySegmenDisabledLayer(true);
		} else {
			if(!opening) {
				if(animation_type) {
					setTimeout(() => {
						setOpeningLoading(true);
					}, 400);
				}

				setOpening(true);

				if(!is_builder) {
					if(props.click_action != undefined) props.click_action();
				} else {
					setTimeout(() => {
						setOpening(false);
						setOpeningLoading(false);
					}, 1500);
				}
			}
		}
	};

	let img_props;
	if(site_layout == 'mosaic') {
		const mosaic_img_props = promo?.stages_mosaic_image_proportion ?? 'landscape';
		switch(mosaic_img_props) {
			case 'squared':
				img_props = '1x1';
				break;
			case 'portrait':
				img_props = '2x3';
				break;
			case 'landscape':
			default:
				img_props = '120x63';
				break;
		}
	} else {
		img_props = '1x1';
	}

	let whole_link = (site_layout == 'list' && can_participate_again && open) ? true : false;
	if(segment_disabled) whole_link = true;

	var classes = ['gui_main_text_color_on_bg'];
	if(segment_disabled) {
		classes.push('is_private');
	} else {
		if(images_for_closed_stage) classes.push(`with_custom_image`);
		classes.push(`is_${stage_state}`);
	}
	if(has_participated) classes.push('already_opened');
	if(opening) classes.push('opening');
	if(opening_loading) classes.push('opening_loading');
	if(whole_link && no_more_participations_completed === false) classes.push('is_whole_link gui_is_animated');

	let next_participation;
	if(participation != null) {
		if(stage.participation_frequency == 'hour') {
			next_participation = minutesToNextHour(participation.next_participation);
		} else {
			next_participation = moment.tz(participation.next_participation_notz, tz).fromNow();
		}
	}

	const link_props = {
		promo_id:promo.id,
		stage,
		is_builder,
		intl:props.intl,
	};

	const status_ribbons = [];
	if(hide_stage_status === false && segment_disabled === false && is_disabled_no_prizes === false) {
		if(open) {
			status_ribbons.push({
				title:props.intl.formatMessage({ id:"{time} left" }, { time:moment.tz(stage.end_date, tz).fromNow(true) }),
				autotranslate:false,
				color:'--ribbon-open-bg-color-rgb',
				text_color:'var(--ribbon-open-text-color-rgb)',
			});
		}
		if(pending) {
			status_ribbons.push({
				title:props.intl.formatMessage({ id:"Opens {time}" }, { time:moment.tz(stage.start_date, tz).fromNow(true) }),
				autotranslate:false,
				color:'--ribbon-pending-bg-color-rgb',
				text_color:'var(--ribbon-pending-text-color-rgb)',
			});
		}
		if(closed) {
			status_ribbons.push({
				title:i18n("closed", 'ucfirst'),
				autotranslate:false,
				color:'--ribbon-closed-bg-color-rgb',
				text_color:'var(--ribbon-closed-text-color-rgb)',
			});
		}
	}

	if(hide_completion_status === false && completed) {
		status_ribbons.push({
			title:"Completed",
			color:'--ribbon-open-bg-color-rgb',
			text_color:'var(--ribbon-open-text-color-rgb)',
		});
	}

	if(!hide_stage_status && is_disabled_no_prizes) {
		status_ribbons.push({
			title:"Sold-Out prizes",
		});
	}

	if(!hide_stage_status && segment_disabled) {
		status_ribbons.push({
			title:"Restricted access",
		});
	}

	return (
		<>
			<div class={classes.join(" ")} onClick={(whole_link) ? () => openStage() : null}>
				{(site_layout == 'mosaic') && (
					<div class={'stage_img' + ((!stage_picture) ? ' default_img' : '')}>
						<StageImage
							can_participate_again={can_participate_again}
							default_promo_color={default_promo_color}
							has_participated={has_participated}
							icon={stage_type.icon_class}
							img_props={img_props}
							in_galleries={in_galleries}
							is_bg={true}
							open={open}
							pending={pending}
							stage_picture={stage_picture}
							stage_title={stage_title}
						/>

						{(open && can_participate_again) && (
							<Spinner color="rgba(var(--main-bg-color), 1)" />
						)}

						{(open && can_participate_again) && (
							<span class="img_shadow" />
						)}

						<StageImage
							can_participate_again={can_participate_again}
							default_promo_color={default_promo_color}
							has_participated={has_participated}
							icon={stage_type.icon_class}
							img_props={img_props}
							in_galleries={in_galleries}
							is_bg={false}
							open={open}
							pending={pending}
							stage_picture={stage_picture}
							stage_title={stage_title}
						/>

						{((open || force_openable || display_closed_gallery) && props.click_action != undefined) && (
							<div class="click_area" onClick={() => openStage()} />
						)}
					</div>
				)}

				{(site_layout == 'list' && list_graphic_display == 'icon') && (
					<div class={stage_type.icon_class} />
				)}

				{(site_layout == 'list' && list_graphic_display == 'image') && (
					<div class={'stage_img' + ((!stage_picture) ? ' default_img' : '')}>
						<StageImage
							can_participate_again={can_participate_again}
							default_promo_color={default_promo_color}
							has_participated={has_participated}
							icon={stage_type.icon_class}
							img_props={img_props}
							in_galleries={in_galleries}
							is_bg={false}
							open={open}
							pending={pending}
							stage_picture={stage_picture}
							stage_title={stage_title}
						/>
					</div>
				)}

				{((stage_title !== false && stage_title != "" && !hide_stage_name) || ((!hide_stage_status || !hide_participation_info) && !in_galleries)) && (
					<div class={'textual_info' + ((stage_description === false) ? ' without_description' : '')}>
						<div class="stage_info">
							{(stage_title !== false) && (
								<h4 class="stage_title">{stage_title}</h4>
							)}

							{(stage.bracket_id != undefined && brackets_list !== false && brackets_list[stage.bracket_id] != undefined) && (
								<div class="bracket_name">
									<p>{brackets_list[stage.bracket_id]}</p>
								</div>
							)}

							{(stage_description !== false) && (
								<h5 class="stage_description">{stage_description}</h5>
							)}
						</div>

						{(props.custom_content != undefined && !in_galleries) && (
							<div class="custom">{props.custom_content}</div>
						)}

						{(!in_galleries) && (
							<div class={'state_info' + ((site_layout == 'list') ? ' gui_not_in_top_tiny' : '')}>
								{(!hide_participation_info && !is_builder && participation != undefined) && (
									<div class="participations_info">
										{(no_more_participations_completed === false) ? (
											<>
												{(participation.next_participation === true || participation.next_participation === false) ? (
													<span>{i18n("Your participations")}: <strong class={(participation.next_participation === false) ? 'hb_mainColorText original_color_item' : null}>{participation.used}/{participation.in_period}</strong></span>
												) : (
													<FormattedMessage id="You'll have more participations {next}" values={{next:<strong>{next_participation}</strong>}} />
												)}
											</>
										) : (
											<span>{i18n("You don't have more participations for this stage.")}</span>
										)}
									</div>
								)}
					
								{(!no_more_participations_completed && !hide_participation_info && is_builder) && (
									<div class="participations_info">
										<span>{i18n("Your participations")}: <strong>0/{stage.max_participations}</strong></span>
									</div>
								)}

								{(!no_more_participations_completed && show_number_participants && !pending) && (
									<div class="participations_info total_participations">
										<span>{i18n("Total participations")}: <strong>{stage?.current_participations ?? '0'}</strong></span>
									</div>
								)}

								{(status_ribbons.length > 0) && (
									<RibbonCollection
										items={status_ribbons}
									/>
								)}
							</div>
						)}

						{(site_layout != 'list') && (
							<div class="stage_actions">
								<ViewStatsLink {...link_props} />

								<ViewResultLink {...link_props} />
							</div>
						)}
					</div>
				)}

				{(site_layout == 'list' && props.click_action != undefined) && (
					<div class="stage_actions">
						<div class="participate_button">
							{(((can_participate_again && open) || force_access) && !is_disabled_no_prizes && !no_more_participations_completed) && (
								<ActionLink
									onClick={() => openStage()}
									autotranslate={false}
								>{enter_button_label}</ActionLink>
							)}

							{((!can_participate_again && !force_access) || no_more_participations_completed) && (
								<div class="no_more gui_small_icon">
									<span
										style={{
											backgroundColor:'rgba(var(--ribbon-open-bg-color))',
											color:'rgba(var(--ribbon-open-text-color))',
										}}
										class="ep-ok"
									/>
								</div>
							)}

							<ViewStatsLink {...link_props} />

							<ViewResultLink {...link_props} />
						</div>
					</div>
				)}
			</div>

			{(display_no_participations_layer) && (
				<FloatingLayerPublic
					title={`<h1>${i18n("No more participations")}</h1>`}
					pm={promo}
					backButton={() => setDisplayNoParticipationsLayer(false)}
				>
					<LeveledHTMLText>
						{/* We add the dangerouslySetInnerHTML to this text because some clients change this label through dictionaries to include some links to other platform pages */}
						<p dangerouslySetInnerHTML={{ __html:i18n("You don't have more participations for this stage.") }} />

						{(participation.next_participation !== true && participation.next_participation !== false) && (
							<p><FormattedMessage id="You'll have more participations {next}" values={{next:<strong>{next_participation}</strong>}} />.</p>
						)}
					</LeveledHTMLText>

					<div class="available_option_links gui_link_actions not_in_top is_no_more_participations">
						<ViewMyParticipationLink {...link_props} />

						<ViewStatsLink {...link_props} />

						<ViewResultLink {...link_props} />

						<ViewGalleryLink {...link_props} />

						<ViewGalleryRankingLink {...link_props} />
					</div>
				</FloatingLayerPublic>
			)}

			{(display_completed_layer) && (
				<FloatingLayerPublic
					title={`<h1>${i18n("Completed")}</h1>`}
					pm={promo}
					backButton={() => setDisplayNoCompletedLayer(false)}
				>
					<LeveledHTMLText>
						{/* We add the dangerouslySetInnerHTML to this text because some clients change this label through dictionaries to include some links to other platform pages */}
						<p dangerouslySetInnerHTML={{ __html:i18n("You already completed this game.") }} />
					</LeveledHTMLText>

					{/* no additional links in da house: */}
					{/*
					<div class="available_option_links gui_link_actions not_in_top is_no_more_participations">
						<ViewMyParticipationLink {...link_props} />

						<ViewStatsLink {...link_props} />

						<ViewResultLink {...link_props} />

						<ViewGalleryLink {...link_props} />

						<ViewGalleryRankingLink {...link_props} />
					</div>
					*/}
				</FloatingLayerPublic>
			)}

			{(display_no_more_prizes_layer) && (
				<FloatingLayerPublic
					title={`<h1>${i18n("Sold-Out prizes")}</h1>`}
					pm={promo}
					backButton={() => setDisplayNoMorePrizesLayer(false)}
				>
					<LeveledHTMLText>
						<p>{i18n("You can no longer participate because all prizes are sold out.")}</p>
					</LeveledHTMLText>
				</FloatingLayerPublic>
			)}

			{(display_closed_gallery_layer && closed) && (
				<FloatingLayerPublic
					title={`<h1>${i18n("Entries closed")}</h1>`}
					pm={promo}
					backButton={() => setDisplayClosedGalleryLayer(false)}
				>
					<LeveledHTMLText>
						<p>{i18n("The participation stage has expired.")}</p>
					</LeveledHTMLText>

					<div class="available_option_links gui_link_actions not_in_top is_closed_entries">
						<ViewMyParticipationLink {...link_props} />

						<ViewStatsLink {...link_props} />

						<ViewResultLink {...link_props} />

						<ViewGalleryLink {...link_props} />

						<ViewGalleryRankingLink {...link_props} />
					</div>
				</FloatingLayerPublic>
			)}

			{(display_segment_disabled_layer) && (
				<FloatingLayerPublic
					title={`<h1>${i18n("Restricted access")}</h1>`}
					pm={promo}
					backButton={() => setDisplaySegmenDisabledLayer(false)}
				>
					<LeveledHTMLText>
						<p dangerouslySetInnerHTML={{ __html:(stage?.disabled_message ?? "You don't have permissions to access this stage as it is restricted.").replace(/\n/g, '<br />')}} />
					</LeveledHTMLText>
				</FloatingLayerPublic>
			)}

			{(display_closed_gallery_layer && !closed) && (
				<FloatingLayerPublic
					title={`<h1>${i18n("Participation closed")}</h1>`}
					pm={promo}
					backButton={() => setDisplayClosedGalleryLayer(false)}
				>
					<LeveledHTMLText>
						<p>{i18n("Participation start date")}: {moment.tz(stage.start_date, tz).format('LLLL')} ({tz})</p>
					</LeveledHTMLText>

					<div class="available_option_links gui_link_actions not_in_top is_closed_stage">
						<ViewGalleryLink {...link_props} />
					</div>
				</FloatingLayerPublic>
			)}
		</>
	);
}

export default injectIntl(StageMosaicItem);